import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MessageSquarePlus } from 'lucide-react';
import type {
  EligiblePlayer,
  MessageThreadSummary,
  PlayerStatus,
} from '@swiss-magic/shared-types';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  getDirectorMessageThreads,
  getEligiblePlayers,
} from '@/lib/api/messages';
import { ApiError } from '@/lib/api/core';
import { usePolledData } from '@/lib/usePolledData';
import { showErrorToast } from '@/lib/toast';
import { LOCALE_LABELS } from '@/lib/i18n';
import type { AnyLocale } from '@swiss-magic/i18n';

export function DirectorMessageCenterPage() {
  const { t } = useTranslation('director');
  const navigate = useNavigate();
  const { id: tournamentId } = useParams<{ id: string }>();

  const { data, loading, message } = usePolledData<MessageThreadSummary[]>(
    () =>
      getDirectorMessageThreads(tournamentId!)
        .then((r) => r.threads)
        .catch((err: unknown) => {
          throw err instanceof ApiError ? new Error(err.userMessage) : err;
        }),
    [tournamentId],
  );
  useEffect(() => {
    if (message) showErrorToast(message);
  }, [message]);

  const threads = data ?? [];

  const [pickerOpen, setPickerOpen] = useState(false);
  const [eligible, setEligible] = useState<EligiblePlayer[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  async function openPicker() {
    setPickerOpen(true);
    setEligible(null);
    setSelected(new Set());
    try {
      const r = await getEligiblePlayers(tournamentId!);
      setEligible(r.players);
    } catch (err) {
      setEligible([]);
      if (err instanceof ApiError) showErrorToast(err.userMessage);
    }
  }

  const reachable = useMemo(
    () => (eligible ?? []).filter((e) => e.reachable && e.playerUserId),
    [eligible],
  );

  const statuses = useMemo(() => {
    const seen = new Set<PlayerStatus>();
    for (const e of reachable) if (e.status) seen.add(e.status);
    return [...seen];
  }, [reachable]);

  const locales = useMemo(() => {
    const seen = new Set<string>();
    for (const e of reachable) if (e.locale) seen.add(e.locale);
    return [...seen];
  }, [reachable]);

  const hasUnknownLocale = useMemo(
    () => reachable.some((e) => !e.locale),
    [reachable],
  );

  function languageLabel(locale: string | null): string {
    if (!locale) return t('messages.picker.language_unknown');
    return LOCALE_LABELS[locale as AnyLocale] ?? locale;
  }

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function quickToggle(matching: EligiblePlayer[]) {
    const ids = matching.map((e) => e.playerUserId!).filter(Boolean);
    setSelected((prev) => {
      const next = new Set(prev);
      const allOn = ids.every((id) => next.has(id));
      for (const id of ids) {
        if (allOn) next.delete(id);
        else next.add(id);
      }
      return next;
    });
  }

  function continueToCompose() {
    const ids = [...selected];
    if (ids.length === 0) return;
    const params = new URLSearchParams();
    for (const id of ids) params.append('playerUserId', id);
    const names: Record<string, string> = {};
    for (const e of reachable) {
      if (e.playerUserId && selected.has(e.playerUserId)) {
        names[e.playerUserId] = e.name;
      }
    }
    setPickerOpen(false);
    navigate(`/tournaments/${tournamentId}/messages/new?${params.toString()}`, {
      state: { recipientNames: names },
    });
  }

  return (
    <div data-testid="director-message-center-page">
      <div className="mb-6 flex items-center justify-between gap-4">
        <h2 className="text-2xl font-semibold">{t('messages.title')}</h2>
        <Button data-testid="director-new-message-button" onClick={openPicker}>
          <MessageSquarePlus className="mr-2 h-4 w-4" />
          {t('messages.new_message')}
        </Button>
      </div>

      {loading ? (
        <p className="text-muted-foreground">{t('messages.loading')}</p>
      ) : threads.length === 0 ? (
        <p data-testid="director-messages-empty" className="text-muted-foreground">
          {t('messages.empty')}
        </p>
      ) : (
        <div className="space-y-3">
          {threads.map((thread) => (
            <Card
              key={thread.id}
              data-testid="director-conversation-row"
              className="cursor-pointer transition-colors hover:bg-muted/50"
              onClick={() =>
                navigate(`/tournaments/${tournamentId}/messages/${thread.id}`)
              }
            >
              <CardContent className="flex items-center justify-between gap-4 p-4">
                <div className="min-w-0">
                  <p className="truncate font-medium">{thread.playerName}</p>
                  {thread.lastMessagePreview && (
                    <p className="truncate text-sm text-muted-foreground">
                      {thread.lastMessagePreview}
                    </p>
                  )}
                </div>
                {thread.unreadCount > 0 && (
                  <Badge data-testid="director-conversation-unread" variant="default">
                    {thread.unreadCount}
                  </Badge>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent data-testid="director-new-message-picker">
          <DialogHeader>
            <DialogTitle>{t('messages.picker.title')}</DialogTitle>
          </DialogHeader>

          {eligible === null ? (
            <p className="text-sm text-muted-foreground">{t('messages.loading')}</p>
          ) : eligible.length === 0 ? (
            <p
              data-testid="director-picker-empty"
              className="text-sm text-muted-foreground"
            >
              {t('messages.picker.empty')}
            </p>
          ) : (
            <>
              {(statuses.length > 0 ||
                locales.length > 0 ||
                hasUnknownLocale) && (
                <div className="flex flex-wrap gap-2 border-b pb-3">
                  {statuses.map((s) => (
                    <Button
                      key={`status-${s}`}
                      type="button"
                      variant="outline"
                      size="sm"
                      data-testid={`director-quickselect-status-${s}`}
                      onClick={() =>
                        quickToggle(reachable.filter((e) => e.status === s))
                      }
                    >
                      {t(`players_page.status.${s}`)}
                    </Button>
                  ))}
                  {locales.map((l) => (
                    <Button
                      key={`lang-${l}`}
                      type="button"
                      variant="outline"
                      size="sm"
                      data-testid={`director-quickselect-language-${l}`}
                      onClick={() =>
                        quickToggle(reachable.filter((e) => e.locale === l))
                      }
                    >
                      {LOCALE_LABELS[l as AnyLocale] ?? l}
                    </Button>
                  ))}
                  {hasUnknownLocale && (
                    <Button
                      key="lang-unknown"
                      type="button"
                      variant="outline"
                      size="sm"
                      data-testid="director-quickselect-language-unknown"
                      onClick={() =>
                        quickToggle(reachable.filter((e) => !e.locale))
                      }
                    >
                      {t('messages.picker.language_unknown')}
                    </Button>
                  )}
                </div>
              )}

              <ul className="max-h-72 divide-y overflow-y-auto">
                {eligible.map((e, i) =>
                  e.reachable && e.playerUserId ? (
                    <li key={e.playerUserId}>
                      <label
                        data-testid="director-picker-option"
                        className="flex cursor-pointer items-center gap-3 px-2 py-3 text-sm hover:bg-muted/50"
                      >
                        <input
                          type="checkbox"
                          className="size-4"
                          checked={selected.has(e.playerUserId)}
                          onChange={() => toggle(e.playerUserId!)}
                        />
                        <span className="min-w-0 flex-1 truncate">{e.name}</span>
                        {e.status && (
                          <Badge
                            data-testid="picker-row-status"
                            variant="secondary"
                          >
                            {t(`players_page.status.${e.status}`)}
                          </Badge>
                        )}
                        <span
                          data-testid="picker-row-language"
                          className="shrink-0 text-xs text-muted-foreground"
                        >
                          {languageLabel(e.locale)}
                        </span>
                      </label>
                    </li>
                  ) : (
                    <li key={`unreachable-${i}`}>
                      <button
                        type="button"
                        disabled
                        data-testid="director-picker-option-disabled"
                        className="flex w-full items-center gap-3 px-2 py-3 text-left text-sm text-muted-foreground"
                      >
                        <span className="min-w-0 flex-1 truncate">{e.name}</span>
                        {e.status && (
                          <Badge
                            data-testid="picker-row-status"
                            variant="secondary"
                          >
                            {t(`players_page.status.${e.status}`)}
                          </Badge>
                        )}
                        <span className="shrink-0 text-xs">
                          {t('messages.picker.no_account')}
                        </span>
                      </button>
                    </li>
                  ),
                )}
              </ul>

              <DialogFooter>
                <Button
                  type="button"
                  data-testid="director-picker-continue"
                  disabled={selected.size === 0}
                  onClick={continueToCompose}
                >
                  {t('messages.picker.continue', { count: selected.size })}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
